import { useState, useEffect } from 'react'
import { useBuilder } from '../context/BuilderContext'
import { Settings, Layout, ShieldCheck, Accessibility } from 'lucide-react'
import TextSettings from './settings/TextSettings'
import HeadingSettings from './settings/HeadingSettings'
import ImageSettings from './settings/ImageSettings'
import ButtonSettings from './settings/ButtonSettings'
import MenuSettings from './settings/MenuSettings'
import ColumnSettings from './settings/ColumnSettings'
import HTMLSettings from './settings/HTMLSettings'
import SocialSettings from './settings/SocialSettings'
import VideoSettings from './settings/VideoSettings'
import TableSettings from './settings/TableSettings'
import BodySettings from './settings/BodySettings'
import SpamChecker from './settings/SpamChecker'
import AccessibilityChecker from './settings/AccessibilityChecker'

const PRIMARY_TABS = [
  { id: 'content', label: 'Content', icon: Settings },
  { id: 'body', label: 'Body', icon: Layout },
]

const TOOL_TABS = [
  { id: 'spam', label: 'Spam', icon: ShieldCheck },
  { id: 'a11y', label: 'A11y', icon: Accessibility },
]

export default function SettingsPanel() {
  const { getSelectedBlock, updateBlock, selectedBlockId } = useBuilder()
  const selectedBlock = getSelectedBlock()
  const [activeTab, setActiveTab] = useState('content')

  // Auto-switch to content tab when a block is selected
  useEffect(() => {
    if (selectedBlockId) {
      setActiveTab('content')
    }
  }, [selectedBlockId])

  const handleUpdate = (updates) => {
    if (selectedBlock) {
      updateBlock(selectedBlock.id, updates)
    }
  }

  const renderBlockSettings = () => {
    if (!selectedBlock) {
      return (
        <div style={{
          padding: '2.5rem 1rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '10px',
            background: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '0.25rem'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </div>
          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500', color: '#64748b' }}>
            Select a block to edit
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
            Click on any component in the canvas
          </p>
        </div>
      )
    }

    switch (selectedBlock.type) {
      case 'text':
        return <TextSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'heading':
        return <HeadingSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'image':
        return <ImageSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'button':
        return <ButtonSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'menu':
        return <MenuSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'columns':
        return <ColumnSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'html':
        return <HTMLSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'social':
        return <SocialSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'video':
        return <VideoSettings block={selectedBlock} onUpdate={handleUpdate} />
      case 'table':
        return <TableSettings block={selectedBlock} onUpdate={handleUpdate} />
      default:
        return (
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            No settings available for this block type
          </div>
        )
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'content':
        return (
          <>
            {selectedBlock && (
              <div style={{
                marginBottom: '1rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: 'var(--color-text-primary)',
                  textTransform: 'capitalize'
                }}>
                  {selectedBlock.type} Settings
                </h3>
              </div>
            )}
            {renderBlockSettings()}
          </>
        )
      case 'body':
        return <BodySettings />
      case 'spam':
        return <SpamChecker />
      case 'a11y':
        return <AccessibilityChecker />
      default:
        return null
    }
  }

  const allTabs = [...PRIMARY_TABS, ...TOOL_TABS]

  const TabButton = ({ tab, isActive }) => {
    const Icon = tab.icon
    return (
      <button
        onClick={() => setActiveTab(tab.id)}
        title={tab.label}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '3px',
          padding: '8px 2px 6px',
          border: 'none',
          borderBottom: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
          background: isActive ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
          color: isActive ? 'var(--color-primary)' : '#94a3b8',
          cursor: 'pointer',
          transition: 'all 0.15s',
          minWidth: 0
        }}
        onMouseOver={(e) => {
          if (!isActive) e.currentTarget.style.color = '#64748b'
        }}
        onMouseOut={(e) => {
          if (!isActive) e.currentTarget.style.color = '#94a3b8'
        }}
      >
        <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
        <span style={{
          fontSize: '0.6rem',
          fontWeight: isActive ? '600' : '500',
          letterSpacing: '0.02em',
          lineHeight: 1,
          whiteSpace: 'nowrap'
        }}>
          {tab.label}
        </span>
      </button>
    )
  }

  return (
    <div style={{
      width: '280px',
      minWidth: '280px',
      backgroundColor: '#ffffff',
      borderLeft: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Tab Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid #e5e7eb',
        flexShrink: 0
      }}>
        {/* Primary Tabs */}
        {PRIMARY_TABS.map(tab => (
          <TabButton key={tab.id} tab={tab} isActive={activeTab === tab.id} />
        ))}

        {/* Separator */}
        <div style={{
          width: '1px',
          alignSelf: 'center',
          height: '20px',
          backgroundColor: '#e5e7eb',
          flexShrink: 0
        }} />

        {/* Tool Tabs */}
        {TOOL_TABS.map(tab => (
          <TabButton key={tab.id} tab={tab} isActive={activeTab === tab.id} />
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '1.25rem'
      }}>
        {renderTabContent()}
      </div>
    </div>
  )
}
